<template>
  <div class="registros-container">
    <h2 class="page-title">Cadastrar Nova Localização</h2>

    <div class="filtro-container">
      <button @click="goBack" class="btn-principal">
        <i class="fas fa-arrow-left"></i> Voltar
      </button>
    </div>

    <div class="form-card">
      <form @submit.prevent="cadastrarLocalizacao">

        <!-- Nome -->
        <div class="form-row">
          <label>Nome da Localização</label>
          <input type="text" v-model="form.nome" required />
        </div>

        <!-- Raio -->
        <div class="form-row">
          <label>Raio (em metros)</label>
          <input type="number" v-model="form.raio_m" min="0" required />
        </div>

        <!-- Pesquisa avançada de endereço -->
        <div class="form-row">
          <label>Pesquisar endereço</label>
          <input
            ref="addressInput"
            type="text"
            v-model="addressQuery"
            @input="debouncedSearch"
            @keydown.down.prevent="moveSelection(1)"
            @keydown.up.prevent="moveSelection(-1)"
            @keydown.enter.prevent="selectHighlighted"
            placeholder="Digite o endereço"
          />

          <!-- backdrop para mobile: fecha a lista ao tocar fora -->
          <div
            v-if="addressSuggestions.length"
            class="suggestions-backdrop"
            @click="addressSuggestions = []"
          ></div>

          <ul v-if="addressSuggestions.length" class="suggestions-list">
            <li
              v-for="(item, index) in addressSuggestions"
              :key="index"
              :class="{ highlighted: index === highlightedIndex }"
              @click="selectAddress(item)"
              @mouseover="highlightedIndex = index"
            >
              {{ item.properties.name }}, {{ item.properties.city || item.properties.state }}
            </li>
          </ul>
        </div>

        <!-- Mapa -->
        <div class="form-row">
          <label>Selecione o local no mapa</label>
          <div class="geo-actions">
            <button type="button" class="btn-ghost" @click="solicitarGeolocalizacao" :disabled="geolocLoading">
              <i class="fas fa-location-arrow"></i>
              {{ geolocLoading ? "Buscando sua localização..." : "Usar minha localização" }}
            </button>
          </div>
          <div id="map" class="map"></div>
          <small class="help">Clique no mapa ou selecione um endereço.</small>
        </div>

        <!-- Lat/Lng -->
        <div class="form-row-inline">
          <div><strong>Latitude:</strong> {{ form.latitude.toFixed(6) }}</div>
          <div><strong>Longitude:</strong> {{ form.longitude.toFixed(6) }}</div>
        </div>

        <!-- Botões -->
        <div class="form-actions">
          <button type="button" @click="goBack" class="btn-principal btn-outline">Cancelar</button>
          <button type="submit" class="btn-principal" :disabled="loading">
            <span v-if="loading">Salvando...</span>
            <span v-else><i class="fas fa-save"></i> Cadastrar</span>
          </button>
        </div>

      </form>

      <div v-if="message" :class="['message', success ? 'success' : 'error']">
        {{ message }}
      </div>
    </div>
  </div>
</template>

<script>
import L from "leaflet"
import { adminService } from "@/services/adminService"

export default {
  name: "CadastrarLocalizacao",
  data() {
    return {
      loading: false,
      success: false,
      message: "",
      map: null,
      marker: null,
      circle: null,
      form: {
        nome: "",
        latitude: 0,
        longitude: 0,
        raio_m: 0
      },
      addressQuery: "",
      addressSuggestions: [],
      highlightedIndex: -1,
      userLocation: null,
      debounceTimer: null,
      geolocLoading: false
    }
  },
  mounted() {
    this.initMap()
    this.solicitarGeolocalizacao()
  },
  methods: {
    goBack() {
      this.$router.back()
    },

    initMap() {
      this.map = L.map("map").setView([-14.235, -51.9253], 4)
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 18,
        attribution: '© OpenStreetMap'
      }).addTo(this.map)

      this.map.on("click", (e) => {
        this.setMarker(e.latlng.lat, e.latlng.lng)
      })
    },

    solicitarGeolocalizacao() {
      if (!navigator.geolocation) {
        alert("Geolocalização não suportada neste dispositivo.")
        return
      }

      this.geolocLoading = true
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords
          this.userLocation = { lat: latitude, lng: longitude }
          this.map.setView([latitude, longitude], 15)
          this.setMarker(latitude, longitude)
          this.geolocLoading = false
        },
        (err) => {
          this.geolocLoading = false
          if (err.code === err.PERMISSION_DENIED) {
            alert("Precisamos da sua permissão para usar a localização. Autorize no navegador e tente novamente.")
          } else {
            alert("Não foi possível obter sua localização. Verifique se o GPS está ativo e tente novamente.")
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      )
    },

    setMarker(lat, lng) {
      this.form.latitude = lat
      this.form.longitude = lng

      if (this.marker) {
        this.marker.setLatLng([lat, lng])
      } else {
        this.marker = L.marker([lat, lng]).addTo(this.map)
      }

      if (this.circle) {
        this.map.removeLayer(this.circle)
      }

      this.circle = L.circle([lat, lng], {
        radius: this.form.raio_m,
        color: "#3b82f6"
      }).addTo(this.map)
    },

    // Debounce: evita flood de requisição
    debouncedSearch() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(this.searchAddress, 300)
    },

    async searchAddress() {
      if (!this.addressQuery || !this.userLocation) {
        this.addressSuggestions = []
        return
      }

      const lat = this.userLocation.lat
      const lon = this.userLocation.lng
      const query = encodeURIComponent(this.addressQuery)
      const url = `https://photon.komoot.io/api/?q=${query}&lat=${lat}&lon=${lon}&limit=5`

      try {
        const res = await fetch(url)
        const data = await res.json()
        this.addressSuggestions = data.features || []
        this.highlightedIndex = 0

        // >>> posição dinâmica da lista no mobile e desktop
        this.$nextTick(() => {
          const inputEl = this.$refs.addressInput
          if (inputEl) {
            const rect = inputEl.getBoundingClientRect()
            document.documentElement.style.setProperty(
              "--suggestions-top",
              `${rect.bottom + 8}px`
            )
          }
        })
      } catch (err) {
        console.error("Erro ao buscar endereço:", err)
      }
    },

    selectAddress(item) {
      const { coordinates } = item.geometry
      this.setMarker(coordinates[1], coordinates[0])
      this.map.setView([coordinates[1], coordinates[0]], 17)
      this.addressQuery = item.properties.name
      this.addressSuggestions = []
    },

    moveSelection(direction) {
      if (!this.addressSuggestions.length) return
      this.highlightedIndex += direction
      if (this.highlightedIndex < 0) {
        this.highlightedIndex = this.addressSuggestions.length - 1
      }
      if (this.highlightedIndex >= this.addressSuggestions.length) {
        this.highlightedIndex = 0
      }
    },

    selectHighlighted() {
      if (
        this.highlightedIndex >= 0 &&
        this.highlightedIndex < this.addressSuggestions.length
      ) {
        this.selectAddress(this.addressSuggestions[this.highlightedIndex])
      }
    },

    async cadastrarLocalizacao() {
      if (!this.form.nome || !this.form.latitude || !this.form.longitude) {
        this.message = "Preencha todos os campos e selecione um ponto no mapa."
        this.success = false
        return
      }

      this.loading = true
      this.message = ""
      try {
        await adminService.createLocalizacao(this.form)
        this.success = true
        this.message = "Localização cadastrada com sucesso!"
        setTimeout(() => this.$router.push({ name: "CadastrarLocalizacao" }), 600)
      } catch (err) {
        console.error("Erro ao cadastrar localização:", err)
        this.success = false
        this.message = err.response?.data?.detail || "Erro ao cadastrar localização"
      } finally {
        this.loading = false
      }
    }
  },

  watch: {
    "form.raio_m"(newVal) {
      if (this.marker && this.map) {
        if (this.circle) this.map.removeLayer(this.circle)
        this.circle = L.circle(this.marker.getLatLng(), {
          radius: newVal,
          color: "#3b82f6"
        }).addTo(this.map)
      }
    }
  }
}
</script>

<style scoped>
.registros-container {
  padding: 2rem 1rem;
  background: #f8fafc;
  min-height: 100vh;
  font-family: "Inter", sans-serif;
}

/* Título */
.page-title {
  text-align: center;
  color: #1e293b;
  margin-bottom: 1.5rem;
  font-size: 1.6rem;
  font-weight: 700;
}

/* Botão voltar */
.filtro-container {
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
}
.btn-principal {
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.6rem 1rem;
  cursor: pointer;
  transition: 0.25s ease;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
.btn-principal:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.3);
}
.btn-outline {
  background-color: transparent;
  color: #3b82f6;
  border: 1px solid #cbd5e1;
}
.btn-outline:hover {
  background: #eff6ff;
}

/* Card do formulário */
.form-card {
  max-width: 640px;
  margin: 0 auto;
  background: white;
  border-radius: 12px;
  padding: 2rem 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

/* Campos */
.form-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 1.25rem;
  position: relative;
}
.form-row label {
  font-weight: 600;
  color: #475569;
  margin-bottom: 0.4rem;
}
.form-row input {
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  font-size: 0.95rem;
  transition: border-color 0.2s ease;
}
.form-row input:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

/* Campos lado a lado */
.form-row-inline {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}
.form-row-inline div {
  background: #f1f5f9;
  padding: 0.6rem 0.8rem;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #334155;
  flex: 1;
  text-align: center;
}

/* Mapa */
.map {
  width: 100%;
  height: 320px;
  border-radius: 10px;
  margin-top: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}
  .help {
  font-size: 0.85rem;
  color: #64748b;
  margin-top: 0.4rem;
  display: block;
}

.geo-actions {
  display: flex;
  justify-content: flex-end;
  margin: 0.25rem 0 0.35rem;
}

.btn-ghost {
  background: #e2e8f0;
  color: #0f172a;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-weight: 600;
}

.btn-ghost:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Lista de sugestões (autocomplete) */
/* AGORA: mobile friendly */
.suggestions-list {
  list-style: none;
  margin: 0;
  padding: 0.25rem 0;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: white;
  box-shadow: 0 4px 10px rgba(0,0,0,0.08);
  max-height: 200px;
  overflow-y: auto;

  position: fixed;              /* <-- antes era absolute */
  left: 50%;
  transform: translateX(-50%);
  width: 90%;                   /* ocupa bem em qualquer tela */
  z-index: 2000;
  top: var(--suggestions-top, 200px); /* setado via JS */
}
.suggestions-list li {
  padding: 0.6rem 0.8rem;
  cursor: pointer;
  transition: background 0.2s ease;
  font-size: 0.95rem;
}
.suggestions-list li:hover,
.suggestions-list li.highlighted {
  background: #eff6ff;
}

/* Backdrop pra tocar fora e fechar no mobile */
.suggestions-backdrop {
  position: fixed;
  inset: 0;
  background: transparent;
  z-index: 1500;
}

/* Botões de ação */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
}

/* Mensagens */
.message {
  margin-top: 1.5rem;
  padding: 0.9rem 1rem;
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
}
.message.success {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
}
.message.error {
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

/* Responsividade */
@media (max-width: 640px) {
  .form-card {
    padding: 1.25rem;
  }

  .form-row-inline {
    flex-direction: column;
    gap: 0.5rem;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn-principal,
  .btn-outline {
    width: 100%;
    justify-content: center;
  }
}
</style>
